import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import { useModal } from '../../hooks/use-modal'
import type { Result, Patient, Device, ResultValue, InstrumentObservation } from '@alpha-edge/shared'
import {
  PageHeader,
  StatusBadge,
  Alert,
  Modal,
  ConfirmDialog,
  FormField,
  card,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
  input,
} from '../../components/ui'

const TRANSITIONS: Record<string, readonly string[]> = {
  pending: ['in_progress', 'canceled'],
  in_progress: ['resulted', 'canceled'],
  resulted: ['verified', 'canceled'],
  verified: ['released'],
  released: [],
  canceled: [],
}

const RESULT_VALUES: readonly ResultValue[] = ['positive', 'negative', 'invalid', 'indeterminate']

export function ResultDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [result, setResult] = useState<Result | null>(null)
  const [patient, setPatient] = useState<Patient | null>(null)
  const [device, setDevice] = useState<Device | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // PLAN.md §7.6.24 Slice 4 -- this result's own matched instrument
  // observation, if any (most recent one that fed this exact result row).
  const [matchedObservation, setMatchedObservation] = useState<InstrumentObservation | null>(null)
  const [cancelReason, setCancelReason] = useState('')

  const recordModal = useModal()
  const archiveModal = useModal()
  const [recordValue, setRecordValue] = useState<ResultValue>('negative')
  const [recordNote, setRecordNote] = useState('')
  const [recordTimestamp, setRecordTimestamp] = useState('')
  const [archiving, setArchiving] = useState(false)

  const fetchResult = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<Result>(`/results/${id}`)
      if (res.data) {
        setResult(res.data)
        setMatchedObservation(null)
        if (res.data.patientId) {
          api
            .get<Patient>(`/patients/${res.data.patientId}`)
            .then((p) => setPatient(p.data ?? null))
            .catch(() => {})
        }
        if (res.data.deviceId) {
          api
            .get<Device>(`/devices/${res.data.deviceId}`)
            .then((d) => setDevice(d.data ?? null))
            .catch(() => {})
        }
        if (res.data.orderId) {
          const orderId = res.data.orderId
          const resultId = res.data.id
          api
            .get<readonly InstrumentObservation[]>(`/orders/${orderId}/instrument-observations`)
            .then((obsRes) => {
              // Sorted receivedAt desc by the API -- the first match is the
              // most recent observation that fed this exact result row.
              const list = Array.isArray(obsRes.data) ? obsRes.data : []
              const match = list.find((o) => o.resultId === resultId)
              setMatchedObservation(match ?? null)
            })
            .catch(() => {
              // non-fatal -- the card just doesn't render
            })
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    }
    setLoading(false)
  }, [id])

  useEffect(() => {
    fetchResult()
  }, [fetchResult])

  const updateStatus = async (status: string, extra?: Record<string, string>) => {
    setError(null)
    try {
      const body: Record<string, string> = { status, ...extra }
      if (status === 'canceled') body.cancelReason = cancelReason || 'Canceled by user'
      await api.put(`/results/${id}`, body)
      await fetchResult()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update')
    }
  }

  const handleRecordResult = async () => {
    await updateStatus('resulted', {
      resultValue: recordValue,
      ...(recordNote ? { note: recordNote } : {}),
      ...(recordTimestamp ? { resultTimestamp: recordTimestamp } : {}),
    })
    recordModal.close()
    setRecordNote('')
    setRecordTimestamp('')
  }

  const handleArchive = async () => {
    setArchiving(true)
    try {
      await api.delete(`/results/${id}`)
      navigate('/results')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive')
      archiveModal.close()
    } finally {
      setArchiving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>
  if (!result) return <div>Result not found.</div>

  const nextStatuses = TRANSITIONS[result.status] ?? []

  return (
    <div>
      <PageHeader
        title={`Result: ${result.barcode || result.id.slice(0, 8)}`}
        subtitle={result.testType}
        backTo="/results"
        backLabel="Back to Results"
        actions={
          <>
            <button onClick={() => window.open(`/api/results/${id}/pdf`, '_blank')} style={btnPrimary}>
              Download PDF
            </button>
            <button onClick={archiveModal.open} style={btnDanger}>
              Archive
            </button>
            <button onClick={() => navigate('/results')} style={btnSecondary}>
              Back to Results
            </button>
          </>
        }
      />
      {error && <Alert type="error" message={error} />}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Details</h3>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: '0.5rem 1rem',
              margin: 0,
              fontSize: '0.875rem',
            }}
          >
            <dt style={{ color: colors.textMuted }}>Status</dt>
            <dd style={{ margin: 0 }}>
              <StatusBadge status={result.status} />
            </dd>
            <dt style={{ color: colors.textMuted }}>Test Type</dt>
            <dd style={{ margin: 0 }}>{result.testType}</dd>
            <dt style={{ color: colors.textMuted }}>Barcode</dt>
            <dd style={{ margin: 0 }}>{result.barcode || '--'}</dd>
            <dt style={{ color: colors.textMuted }}>Value</dt>
            <dd style={{ margin: 0 }}>{result.resultValue || '--'}</dd>
            <dt style={{ color: colors.textMuted }}>Patient</dt>
            <dd style={{ margin: 0 }}>
              {patient ? (
                <a href={`/patients/${patient.id}`} style={{ color: colors.primary }}>
                  {patient.lastName}, {patient.firstName} ({patient.medicalRecordNumber || 'No MRN'})
                </a>
              ) : (
                '--'
              )}
            </dd>
            <dt style={{ color: colors.textMuted }}>Device</dt>
            <dd style={{ margin: 0 }}>{device ? `${device.name} (${device.serialNumber})` : '--'}</dd>
            <dt style={{ color: colors.textMuted }}>Result Time</dt>
            <dd style={{ margin: 0 }}>
              {result.resultTimestamp ? new Date(result.resultTimestamp).toLocaleString() : '--'}
            </dd>
            <dt style={{ color: colors.textMuted }}>Created</dt>
            <dd style={{ margin: 0 }}>{new Date(result.createdAt).toLocaleString()}</dd>
            {result.verifiedAt && (
              <>
                <dt style={{ color: colors.textMuted }}>Verified</dt>
                <dd style={{ margin: 0 }}>{new Date(result.verifiedAt).toLocaleString()}</dd>
              </>
            )}
            {result.releasedAt && (
              <>
                <dt style={{ color: colors.textMuted }}>Released</dt>
                <dd style={{ margin: 0 }}>{new Date(result.releasedAt).toLocaleString()}</dd>
              </>
            )}
            {result.note && (
              <>
                <dt style={{ color: colors.textMuted }}>Note</dt>
                <dd style={{ margin: 0 }}>{result.note}</dd>
              </>
            )}
          </dl>
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Workflow</h3>
          {nextStatuses.length === 0 ? (
            <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>No further actions available.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {nextStatuses
                .filter((s) => s !== 'canceled')
                .map((s) => (
                  <button
                    key={s}
                    onClick={s === 'resulted' ? recordModal.open : () => updateStatus(s)}
                    style={btnPrimary}
                  >
                    {s === 'in_progress'
                      ? 'Start Processing'
                      : s === 'resulted'
                        ? 'Record Result'
                        : s === 'verified'
                          ? 'Verify'
                          : s === 'released'
                            ? 'Release'
                            : s.replace(/_/g, ' ')}
                  </button>
                ))}
              {nextStatuses.includes('canceled') && (
                <>
                  <div style={{ borderTop: `1px solid ${colors.border}`, margin: '0.5rem 0' }} />
                  <input
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      boxSizing: 'border-box',
                    }}
                    placeholder="Cancel reason..."
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                  <button onClick={() => updateStatus('canceled')} style={btnDanger}>
                    Cancel Result
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {matchedObservation && (
        <div style={{ ...card, marginTop: '1rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Instrument Data</h3>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: '0.5rem 1rem',
              margin: 0,
              fontSize: '0.875rem',
            }}
          >
            <dt style={{ color: colors.textMuted }}>Raw Value</dt>
            <dd style={{ margin: 0 }}>
              {matchedObservation.rawValue ?? '--'}
              {matchedObservation.unit ? ` ${matchedObservation.unit}` : ''}
            </dd>
            <dt style={{ color: colors.textMuted }}>Reference Range</dt>
            <dd style={{ margin: 0 }}>
              {matchedObservation.refRange ? JSON.stringify(matchedObservation.refRange) : '--'}
            </dd>
            <dt style={{ color: colors.textMuted }}>Received</dt>
            <dd style={{ margin: 0 }}>
              {matchedObservation.receivedAt ? new Date(matchedObservation.receivedAt).toLocaleString() : '--'}
            </dd>
          </dl>
          {result.orderId && (
            <div style={{ marginTop: '0.75rem' }}>
              <a href={`/orders/${result.orderId}`} style={{ color: colors.primary, fontSize: '0.875rem' }}>
                View all instrument data for this order
              </a>
            </div>
          )}
        </div>
      )}

      <Modal isOpen={recordModal.isOpen} onClose={recordModal.close} title="Record Result">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <FormField name="resultValue" label="Result Value" required>
            <select style={input} value={recordValue} onChange={(e) => setRecordValue(e.target.value as ResultValue)}>
              {RESULT_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </FormField>
          <FormField
            name="resultTimestamp"
            label="Device Time (optional)"
            type="datetime-local"
            value={recordTimestamp}
            onChange={setRecordTimestamp}
          />
          <FormField name="note" label="Note (optional)" multiline value={recordNote} onChange={setRecordNote} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button style={btnSecondary} onClick={recordModal.close}>
              Cancel
            </button>
            <button style={btnPrimary} onClick={handleRecordResult}>
              Save Result
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Result"
        message="Are you sure you want to archive this result? It will be excluded from search results."
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
